import { TourStopButtonAction } from '../models/tour-stop-button-action';

/**
 * Attaches event listeners to tour stop button elements.
 *
 * This utility function scans the provided wrapper element for elements with
 * `data-tour-action-key` attributes and attaches click event listeners based
 * on the provided button actions configuration. It also updates the button
 * text and removes buttons that don't have corresponding actions.
 *
 * @param buttonActions - Configuration object mapping action keys to button actions
 * @param wrapper - The DOM element to scan for actionable elements
 *
 * @example
 * ```typescript
 * const buttonActions = {
 *   nextButton: {
 *     displayText: 'Continue',
 *     listener: (elements) => console.log('Next clicked')
 *   },
 *   skipButton: {
 *     displayText: 'Skip',
 *     listener: (elements) => console.log('Skip clicked')
 *   }
 * };
 *
 * // HTML: <button data-tour-action-key="nextButton"></button>
 * addTourStopEventListeners(buttonActions, wrapperElement);
 * // Button text becomes "Continue" and click handler is attached
 * ```
 *
 * @example
 * ```typescript
 * // Elements without matching actions are removed
 * const buttonActions = { nextButton: { displayText: 'Next', listener: () => {} } };
 *
 * // HTML:
 * // <button data-tour-action-key="nextButton"></button>
 * // <button data-tour-action-key="cancelButton"></button>
 *
 * addTourStopEventListeners(buttonActions, wrapper);
 * // The cancelButton element is removed since no action is configured
 * ```
 */
export function addTourStopEventListeners(
  buttonActions: Record<string, null | TourStopButtonAction | undefined>,
  wrapper: HTMLElement
) {
  const actionElements: Record<string, HTMLElement> = {};

  // Find all elements with tour action keys
  const elements: HTMLElement[] = Array.from(
    wrapper.querySelectorAll('[data-tour-action-key]')
  );

  for (const element of elements) {
    const actionKey = element.dataset['tourActionKey'];

    // Skip elements without valid action keys
    if (typeof actionKey !== 'string' || actionKey.length === 0) {
      continue;
    }

    const action = buttonActions[actionKey];
    if (action) {
      // Store element reference for listener callbacks
      actionElements[actionKey] = element;

      // Update button text and attach click listener
      element.innerHTML = action.displayText;
      element.addEventListener('click', () => {
        action.listener(actionElements);
      });
    } else {
      // Remove elements that don't have corresponding actions
      element.remove();
    }
  }
}
